import React, { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import type { ApolloError } from '@apollo/client';
import { useQuery } from '@apollo/client';

import useSearchParameters from 'bundles/search-common/providers/useSearchParameters';
import MediumIntent from 'bundles/search-common/queries/MediumIntent.graphql';
import type {
  MediumIntentQuery,
  MediumIntentQueryVariables,
} from 'bundles/search-common/queries/__generated__/MediumIntent';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';

export type MediumIntentData = {
  matchedEntity: string;
  entityType: string;
  intent: string;
};

/**
 * Hook to fetch medium intent data using the prod_search_queries_to_entities job
 * @param query - Search query string to get medium intent for
 * @returns Object containing medium intent data and loading state
 */
function useMediumIntent(query: string, isConsumerSerp: boolean) {
  const { data, loading, error } = useQuery<MediumIntentQuery, MediumIntentQueryVariables>(MediumIntent, {
    variables: {
      jobName: 'prod_search_queries_to_entities',
      query,
    },
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
    skip: !query || !isConsumerSerp,
  });

  const content = data?.NaptimeQueries?.ExternallyAccessibleNostosV1Resource?.getAllProperties?.elements?.[0]?.content;

  let mediumIntentData: MediumIntentData | null = null;

  if (content && typeof content === 'object' && content !== null) {
    // Content is already an object, check if it's valid medium intent
    if (content.matchedEntity && content.entityType && content.intent === 'medium') {
      mediumIntentData = {
        matchedEntity: content.matchedEntity,
        entityType: content.entityType,
        intent: content.intent,
      };
    }
  }

  return {
    mediumIntentData,
    loading,
    error,
  };
}

type MediumIntentContextType = {
  mediumIntentData: MediumIntentData | null;
  loading: boolean;
  error: ApolloError | undefined;
};

const MediumIntentContext = createContext<MediumIntentContextType | undefined>(undefined);

export const useMediumIntentContext = () => {
  const context = useContext(MediumIntentContext);
  if (context === undefined) {
    throw new Error('useMediumIntentContext must be used within a MediumIntentProvider');
  }
  return context;
};

type Props = {
  children: ReactNode;
};

export const MediumIntentProvider = ({ children }: Props) => {
  const params = useSearchParameters();
  const query = params.query?.trim().toLowerCase();
  const isConsumerSerp = useIsConsumerSerp();
  const { mediumIntentData, loading, error } = useMediumIntent(query || '', isConsumerSerp);

  const value: MediumIntentContextType = {
    mediumIntentData,
    loading,
    error,
  };

  return <MediumIntentContext.Provider value={value}>{children}</MediumIntentContext.Provider>;
};
